import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { drawCharacter, type AvatarConfig, type Facing } from '@/features/digitalOffice/avatars';

interface AvatarPreviewProps {
  config: AvatarConfig;
  /** CSS pixel size of the (square) preview. */
  size?: number;
  facing?: Facing;
  /** Gentle idle animation (used in the creator). */
  animate?: boolean;
  className?: string;
}

/**
 * Renders a single character on a small canvas using the exact same
 * routine the game canvas uses, so "what you see is what you get".
 */
export function AvatarPreview({
  config,
  size = 64,
  facing = 'down',
  animate = false,
  className,
}: AvatarPreviewProps) {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    const start = performance.now();
    const scale = size / 40;

    const draw = (now: number) => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, size, size);
      const t = (now - start) / 1000;
      drawCharacter(ctx, config, {
        x: size / 2,
        y: size * 0.86,
        scale,
        facing,
        walkPhase: animate ? (t * 0.9) % 1 : undefined,
      });
      if (animate) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [config, size, facing, animate]);

  return (
    <canvas
      ref={ref}
      style={{ width: size, height: size }}
      className={cn('block', className)}
      aria-hidden
    />
  );
}
